<template>
  <div v-if="!billingMaintenanceFeatureFlagEnabled(currentAppConfig)">
    <div v-if="isLoading" class="d-flex justify-content-center mb-3 pt-4">
      <b-spinner style="width: 5rem; height: 5rem" variant="warning" label="Spinning"></b-spinner>
    </div>
    <div v-else>
      <div class="ml-3 mr-3" rounded="lg" id="overlay">
        <div class="row no-gutters mb-2 mt-4">
          <div class="col">
            <h1 class="mb-0 pb-0" data-test-hook="pageTitle">
              <i class="far fa-badge-dollar pr-1" data-test-hook="titleIcon" />
              <span data-test-hook="subscription-plan-details">{{ $t('xManageYourPlan') }}</span>
            </h1>
          </div>
        </div>
        <div v-if="showSubscriptionProcessingBanner">
          <b-row class="pl-1">
            <b-col cols="12" class="pt-3">
              <b-alert show class="alert alert-info mb-0">
                <b-row class="mb-2"
                  ><i class="far fa-circle-exclamation ml-2" />&nbsp;&nbsp;{{
                    $t('xProcessingSubscriptionInformation')
                  }}
                </b-row>
                <span class="ml-3" style="font-weight: normal !important">{{ $t('xProcessingPlanChange') }}</span>
              </b-alert>
            </b-col>
          </b-row>
        </div>

        <DunningSubscriptionBanner v-if="dunningSubscription" :dunningSubscription="dunningSubscription" />

        <div v-for="(expiredApparatus, index) in expiredDealerManagedApparatuses" :key="'expired-' + index">
          <ExpiredDealerManagedBanner :expiredDealerManagedApparatus="expiredApparatus" />
        </div>

        <AccountNeedsAttentionBanner
          v-if="
            (numberOfApparatusInNeedOfAttention > 0 ||
              (apparatusMultiplePlans !== null && premiumSubscriptionApparatusMultiplePlans)) &&
              isAccountAddressAmericaOrCanada
          "
          :productsOnBasicSubscriptionOrNotInPlan="productsOnBasicSubscriptionOrNotInPlan"
          :apparatusesNeedsAttention="premiumApparatusesNeedsAttention"
          :apparatusHasMultipleSubscriptions="apparatusMultiplePlans"
          :premiumSubscriptionApparatusMultiplePlans="premiumSubscriptionApparatusMultiplePlans"
          :allValidApparatusesOnAccount="apparatuses"
          :currentSubscription="
            subsForPremiumPlanCard?.filter(
              sub => sub.subscriptionStatus !== subscriptionDetailCanceled && sub.subscriptionStore !== dealerStore
            )[0]
          "
          :subscriptionDetails="subscriptionDetails"
          @openCancelPlanMultiplePlansModal="openCancelPlanMultiplePlansModal"
          @openRemoveApparatusMultiplePlansModal="openRemoveApparatusMultiplePlansModal"
          :disableUpgradeButton="showSubscriptionProcessingBanner || subscriptionCancelInitiated === true"
        />
        <NoProductsOnAccount v-if="!doesAccountHaveAnyProducts() && !hasPendingCanceledSub()" />

        <div v-for="(productDealerManaged, index) in productsDealerManaged" :key="index">
          <DealerManaged :productDealerManaged="productDealerManaged" />
        </div>

        <LegacySubscriptionList
          v-if="legacySubscriptionList?.length > 0"
          :subscriptionList="legacySubscriptionList !== undefined ? legacySubscriptionList : []"
          @openCancelSubscriptionModal="openCancelSubscriptionModal"
        />
        <PremiumPlan
          v-if="this.subsForPremiumPlanCard?.length > 0 || (!doesAccountHaveAnyProducts() && hasPendingCanceledSub())"
          :subscription="
            subsForPremiumPlanCard?.filter(sub => sub.subscriptionStatus !== subscriptionDetailCanceled)[0]
          "
          :apparatusesNeedsAttention="premiumApparatusesNeedsAttention"
          :premiumSubscriptionApparatusMultiplePlans="premiumSubscriptionApparatusMultiplePlans"
          @openCancelPlanMultiplePlansModal="openCancelPlanMultiplePlansModal"
          @openManagePlanModal="openManagePlanModal"
        />

        <HasProductNoSubscription
          v-if="productsOnBasicSubscriptionOrNotInPlan?.length > 0 && !hasPartiallyEnrolledTank(apparatuses)"
          :productsOnBasicSubscriptionOrNotInPlan="productsOnBasicSubscriptionOrNotInPlan"
          :apparatusesOnExistingSubscription="apparatusesOnExistingSubscription"
          :legacySubscriptionList="legacySubscriptionList"
          :accountProfile="accountProfile"
          :showBasicSubscriptionCard="showBasicSubscriptionCard"
          :disableUpgradeButton="showSubscriptionProcessingBanner"
        />

        <div v-for="(v1FuelMonitor, index) in v1FuelMonitors" :key="index">
          <HasV1FuelMonitor :v1FuelMonitor="v1FuelMonitor" />
        </div>

        <CancelSubscriptionModal
          :isCancelLegacy="isCancelLegacy"
          :endDate="endDate"
          :cancelLegacyApparatusId="cancelLegacyApparatusId !== undefined ? cancelLegacyApparatusId : null"
          :cancelLegacyApparatusType="cancelLegacyApparatusType !== undefined ? cancelLegacyApparatusType : null"
          :cancelSubscriptionApparatuses="cancelSubscriptionApparatuses !== null ? cancelSubscriptionApparatuses : []"
          :cancelSubscriptionProductId="cancelSubscriptionProductId !== undefined ? cancelSubscriptionProductId : null"
          :cancelSubscriptionStore="cancelSubscriptionStore !== undefined ? cancelSubscriptionStore : null"
        />
        <CancelPremiumPlanApparatusMultiplePlansModal
          :accountProfile="accountProfile"
          :apparatus="apparatusMultiplePlans"
          :currentPremiumSubscription="premiumSubscriptionApparatusMultiplePlans"
          :immediatelyOpen="this.apparatusMultipleSubs !== undefined && this.apparatusMultipleSubs !== null"
        />
        <AccountNeedsAttentionModal />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import * as Constants from '@/constants'

import {
  billingMaintenanceFeatureFlagEnabled,
  calculateSubscriptionEndDate,
  checkAndClearCompletedPendingChanges
} from '../../../utils/mixins'

import AccountNeedsAttentionBanner from './AccountNeedsAttentionBanner.vue'
import ExpiredDealerManagedBanner from './ExpiredDealerManagedBanner.vue'
import AccountNeedsAttentionModal from './AccountNeedsAttentionModal.vue'
import CancelPremiumPlanApparatusMultiplePlansModal from './CancelPremiumPlanApparatusMultiplePlansModal.vue'
import CancelSubscriptionModal from './CancelSubscriptionModal.vue'
import DealerManaged from './DealerManaged.vue'
import DunningSubscriptionBanner from './DunningSubscriptionBanner.vue'
import HasProductNoSubscription from './HasProductNoSubscription.vue'
import NoProductsOnAccount from './NoProductsOnAccount.vue'
import LegacySubscriptionList from './LegacySubscriptionList.vue'
import PremiumPlan from './PremiumPlan.vue'
import HasV1FuelMonitor from './HasV1FuelMonitor.vue'
import { computed } from 'vue'
import { hasPartiallyEnrolledTank, tankHasAllProperties } from '../../../utils/apparatus.js'

/** Main page for subscription-related settings and details. */
export default {
  components: {
    AccountNeedsAttentionBanner,
    ExpiredDealerManagedBanner,
    AccountNeedsAttentionModal,
    CancelPremiumPlanApparatusMultiplePlansModal,
    CancelSubscriptionModal,
    DealerManaged,
    DunningSubscriptionBanner,
    HasProductNoSubscription,
    LegacySubscriptionList,
    NoProductsOnAccount,
    PremiumPlan,
    PremiumPlan,
    HasV1FuelMonitor
  },
  props: ['apparatusMultipleSubs'],
  mixins: [validationMixin],
  data() {
    return {
      apparatuses: [],
      cancelLegacyApparatusId: null,
      cancelLegacyApparatusType: null,
      cancelSubscriptionProductId: null,
      cancelSubscriptionStore: null,
      cancelSubscriptionApparatuses: [],
      endDate: null,
      numberOfApparatusInNeedOfAttention: 0,
      isCancelLegacy: false,
      isLoading: true,
      legacySubscriptionList: [],
      premiumApparatusesNeedsAttention: [],
      dunningSubscription: null,
      productsDealerManaged: [],
      v1FuelMonitors: [],
      productsOnBasicSubscriptionOrNotInPlan: [],
      apparatusesOnExistingSubscription: [],
      subscriptions: [],
      subscriptionDetails: null,
      apparatusMultiplePlans: null,
      premiumSubscriptionApparatusMultiplePlans: null,
      subsForPremiumPlanCard: [],
      expiredDealerManagedApparatuses: []
    }
  },
  computed: {
    ...mapState({
      apparatusesFromState: state => state.apparatus.apparatuses,
      accountProfile: state => state.account.accountProfile,
      subscriptionCancelInitiated: state => state.subscriptions.subscriptionCancelInitiated,
      currentAppConfig: state => state.application.currentAppConfig
    }),
    dealerManagedProductId() {
      return Constants.DealerManagedProductId
    },
    subscriptionDetailCanceled() {
      return Constants.SubscriptionDetailCanceled
    },
    tankUtilityDealerManagedProductId() {
      return Constants.TankUtilityDealerManagedProductId
    },
    webStore() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Web')
    },
    appleStore() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Apple')
    },
    googleStore() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Google')
    },
    dealerStore() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer')
    },
    showBasicSubscriptionCard() {
      return (
        this.productsOnBasicSubscriptionOrNotInPlan?.length > 0 &&
        this.subsForPremiumPlanCard?.length === 0 &&
        !this.legacySubscriptionList?.some(
          legacySub => legacySub.subscriptionStatus !== Constants.SubscriptionCancelled
        )
      )
    },
    /** Show blue subscription pending banner if there are any stored pending changes for which we are waiting for a platform acknowledgement. */
    showSubscriptionProcessingBanner() {
      return this.$store.getters.pendingSubscriptionChanges?.length > 0
    },
    /** ML-2283 - Determine if account address is US or Canada. If not, hide the account needs attention banner */
    isAccountAddressAmericaOrCanada() {
      if (this.accountProfile) {
        return Constants.CountriesGetPaidSubs.includes(this.accountProfile?.address?.country)
      }

      // default to true to show the subscription banner
      return true
    }
  },
  methods: {
    getSubForPremiumPlanCard() {
      this.subsForPremiumPlanCard = this.subscriptions?.filter(
        subscription =>
          subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled &&
          !this.isDealerManaged(subscription)
      )
    },
    isDealerManaged(sub) {
      return (
        sub.subscriptionStore === Constants.DealerSubscriptionStore ||
        sub.subscriptionProductId === Constants.DealerManagedProductId ||
        sub.subscriptionProductId === Constants.TankUtilityDealerManagedProductId
      )
    },
    /** Unused. */
    isAnyActiveSubscriptions() {
      return this.subscriptions.some(sub => sub.subscriptionStatus !== Constants.SubscriptionDetailCanceled)
    },
    openCancelSubscriptionModal(request) {
      this.isCancelLegacy = request.isCancelLegacy
      this.endDate = request.endDate
      this.cancelLegacyApparatusId = request.cancelLegacyApparatusId
      this.cancelLegacyApparatusType = request.cancelLegacyApparatusType
      this.openModal('cancel-subscription-modal')
    },
    openCancelPlanMultiplePlansModal() {
      switch (this.premiumSubscriptionApparatusMultiplePlans?.subscriptionStore) {
        case Constants.AppleSubscriptionStore:
        case Constants.GoogleSubscriptionStore:
          this.openModal('apparatus-with-multiple-subscriptions-mobile-platform')
          break
        case Constants.WebSubscriptionStore:
          this.openModal('cancel-single-apparatus-multiple-subscriptions')
          break
        default:
          break
      }
    },
    openRemoveApparatusMultiplePlansModal() {
      switch (this.premiumSubscriptionApparatusMultiplePlans?.subscriptionStore) {
        case Constants.AppleSubscriptionStore:
        case Constants.GoogleSubscriptionStore:
          this.openModal('remove-apparatus-with-multiple-subscriptions-mobile-platform')
          break
        case Constants.WebSubscriptionStore:
          this.openModal('remove-home-premium-apparatus-multiple-subscriptions')
          break
        default:
          break
      }
    },
    hasPendingPlanChangeOnAccount() {
      if (this.subsForPremiumPlanCard?.length > 0) {
        const pendingPlanChangeOnAccount = this.subsForPremiumPlanCard.find(
          subscription => subscription.subscriptionStatus === Constants.SubscriptionDetailPendingProductChange
        )
        return !!pendingPlanChangeOnAccount
      }
      return false
    },
    openManagePlanModal() {
      if (this.hasPendingPlanChangeOnAccount()) {
        this.openModal('pending-plan-change-modal-manage-plan')
      } else {
        this.openModal('manage-plan-modal')
      }
    },
    hasPartiallyEnrolledTank,
    /** Unused */
    doesAccountHaveSubscription() {
      if (!this.subscriptions) return false
      return this.subscriptions.some(sub => sub.status !== Constants.SubscriptionDetailCanceled)
    },
    /**
     * Returns whether or not the given apparatus has a plan or not.
     * If dealer managed, returns false.
     * If V1 FM, returns false.
     * If the apparatus has a non-cancelled subscription, returns false.
     * If the apparatus has a non-cancelled legacy plan, returns false.
     */
    isApparatusBasicOrNotOnPlan(apparatus) {
      if (
        apparatus.isDealerManaged ||
        this.isApparatusV1FuelTank(apparatus) ||
        this.hasActiveLegacySubscription(apparatus?.apparatusId) ||
        this.hasActiveSubscription(apparatus?.apparatusId)
      ) {
        return false
      }
      var dealerManaged = this.subscriptions?.find(x =>
        x.apparatuses?.find(y => y.apparatusId == apparatus?.apparatusId && y.isDealerManaged)
      )
      // This should never be true since DM subs are considered active subscriptions
      if (dealerManaged) {
        return false
      }
      return true
    },
    isApparatusV1FuelTank(apparatus) {
      const deviceType = apparatus.properties?.find(x => x.type === Constants.ApparatusPropertyDevice)?.value
        ?.deviceType
      if (!deviceType) return false
      if (deviceType === Constants.TankUtilityWifiDeviceType || deviceType === Constants.TankUtilityLteDeviceTypeV1)
        return true
      return false
    },
    /** Unused */
    doesAccountHaveApparatusNotOnAnyPlan() {
      const t = this.apparatuses?.find(
        x =>
          !this.hasActiveSubscription(x.apparatusId) &&
          !this.hasActiveLegacySubscription(x.apparatusId)(
            x.type === Constants.HsbApparatusType ||
              (x.type === Constants.FuelTankApparatusType &&
                x.properties?.find(x => x.type === Constants.ApparatusPropertyDevice)?.value?.deviceType ===
                  Constants.TankUtilityLteDeviceTypeV2)
          )
      )
      return t ? true : false
    },
    doesAccountHaveAnyProducts() {
      return this.apparatuses?.length > 0
    },
    doesApparatusHaveSubscription(apparatusId) {
      return this.subscriptions.some(subscription =>
        subscription.apparatuses.some(
          apparatus =>
            apparatus.id === apparatusId && subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled
        )
      )
    },
    getNumberOfApparatusInNeedOfAttention() {
      // NOT NEED ATTENTION
      // 1-2 gens, on or off plan

      // NEEDS ATTENTION
      // 1-2 gens, on or off plan with 1-4 FM not on plan
      // 0 gens with 1-4 FM not on plan
      // 1 hsb on plan with 1 FM that didn't finish enrollment or was removed from DM
      // 1 hsb on plan with 1 hsb removed from DM or not on a plan
      // FM that are on a plan and didn't finish enrollment

      const apparatusesWithActiveLegacySubs = this.apparatuses
        ?.filter(apparatus => {
          return this.legacySubscriptionList?.some(
            legacySub =>
              legacySub.apparatusId === apparatus.apparatusId &&
              legacySub.subscriptionStatus !== Constants.SubscriptionCancelled
          )
        })
        .map(apparatus => apparatus.apparatusId)

      const apparatusesOnActiveNonDealerSubscriptions = this.apparatuses
        ?.filter(apparatus => {
          return this.subscriptions?.some(
            subscription =>
              subscription.apparatuses.some(subApparatus => subApparatus.id === apparatus.apparatusId) &&
              subscription.subscriptionStore !== Constants.DealerSubscriptionStore &&
              subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled
          )
        })
        .map(apparatus => apparatus.apparatusId)

      const apparatusesOnActiveDealerSubscriptions = this.apparatuses
        ?.filter(apparatus => {
          return this.subscriptions?.some(
            subscription =>
              subscription.apparatuses.some(subApparatus => subApparatus.id === apparatus.apparatusId) &&
              subscription.subscriptionStore === Constants.DealerSubscriptionStore &&
              subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled
          )
        })
        .map(apparatus => apparatus.apparatusId)

      const hsbsOnANonDealerPlan = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.HsbApparatusType &&
          (apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) ||
            apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId))
      )

      const hsbNotOnAPlan = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.HsbApparatusType &&
          !apparatus.isDealerManaged &&
          !apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) &&
          !apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId) &&
          !apparatusesOnActiveDealerSubscriptions.includes(apparatus.apparatusId)
      )

      const hsbOnCanceledDM = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.HsbApparatusType &&
          this.subscriptions?.some(
            subscription =>
              subscription.apparatuses.some(subApparatus => subApparatus.id === apparatus.apparatusId) &&
              subscription.subscriptionProductId === Constants.DealerManagedProductId &&
              subscription.subscriptionStatus === Constants.SubscriptionDetailCanceled
          )
      )

      const fmsOnANonDealerPlan = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.FuelTankApparatusType &&
          !this.isApparatusV1FuelTank(apparatus) &&
          (apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) ||
            apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId))
      )

      const fmsNotOnPlan = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.FuelTankApparatusType &&
          !this.isApparatusV1FuelTank(apparatus) &&
          !apparatus.isDealerManaged &&
          !apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) &&
          !apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId)
      )

      const fmOnCanceledDM = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.FuelTankApparatusType &&
          !this.isApparatusV1FuelTank(apparatus) &&
          !apparatus.isDealerManaged &&
          this.subscriptions?.some(
            subscription =>
              subscription.apparatuses.some(subApparatus => subApparatus.id === apparatus.apparatusId) &&
              subscription.subscriptionProductId === Constants.DealerManagedProductId &&
              subscription.subscriptionStatus === Constants.SubscriptionDetailCanceled
          )
      )

      // fuel monitor(s) that are on a plan but haven't finished tank enrollment (missing FuelType/Orientation/Capacity)
      const fmsOnPlanPartiallyEnrolled = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.FuelTankApparatusType &&
          !tankHasAllProperties(apparatus) &&
          (apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) ||
            apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId))
      )

      // NOT NEED ATTENTION
      // 1-2 gens, on or off plan
      var response = 0

      // NEEDS ATTENTION
      // 1-2 gens, on or off plan with 1-4 FM not on plan
      // 0 gens with 1-4 FM not on plan
      // 1 hsb on plan with 1 FM that didn't finish enrollment or was removed from DM
      if (fmsNotOnPlan?.length > 0 || fmOnCanceledDM?.length > 0 || fmsOnPlanPartiallyEnrolled?.length > 0) {
        response = fmsNotOnPlan?.length + fmOnCanceledDM?.length + fmsOnPlanPartiallyEnrolled?.length
      }

      // apparatus(es) on plan with hsb(s) removed from DM or not on a plan
      if (
        (hsbsOnANonDealerPlan?.length > 0 || fmsOnANonDealerPlan?.length > 0) &&
        (hsbOnCanceledDM?.length > 0 || hsbNotOnAPlan?.length > 0)
      ) {
        response += hsbOnCanceledDM?.length + hsbNotOnAPlan?.length
      }

      return response
    },
    /** Returns data about a dunning/grace period subscription if one exists. Otherwise returns nothing. */
    getDunningSubscription() {
      const iapGracePeriodSub = this.subscriptions?.find(
        subscription =>
          subscription.subscriptionStore !== Constants.DealerSubscriptionStore &&
          subscription?.subscriptionReason === Constants.SubscriptionDetailsReasonGracePeriodStarted
      )
      const dunningLegacySub = this.legacySubscriptionList?.find(
        legacySub => legacySub?.subscriptionDunningState === Constants.DunningInProgress
      )

      if (iapGracePeriodSub) {
        return {
          subscriptionStore: iapGracePeriodSub?.subscriptionStore,
          subscriptionDunningDate: iapGracePeriodSub?.subscriptionGracePeriodDate
        }
      } else if (dunningLegacySub) {
        return {
          subscriptionStore: dunningLegacySub?.subscriptionStore,
          subscriptionDunningDate: dunningLegacySub?.subscriptionGracePeriodEndDate
        }
      }
    },
    getPremiumApparatusesNeedsAttention() {
      let notInSubscriptionsList = this.apparatuses?.filter(
        apparatus =>
          !this.doesApparatusHaveSubscription(apparatus.apparatusId) &&
          !apparatus.isDealerManaged &&
          !this.isApparatusV1FuelTank(apparatus) &&
          Constants.CountriesGetPaidSubs.includes(apparatus.country.toUpperCase())
      )

      if (
        !this.subscriptions?.some(
          x =>
            x.subscriptionStatus === Constants.SubscriptionDetailActive &&
            x.subscriptionProductId !== Constants.DealerManagedProductId
        ) &&
        notInSubscriptionsList?.every(apparatus => apparatus.type === Constants.HsbApparatusType) &&
        notInSubscriptionsList?.length <= 2 &&
        this.legacySubscriptionList?.length < 1
      ) {
        return []
      }

      if (
        this.subscriptions?.some(x => x.subscriptionStatus === Constants.SubscriptionDetailActive) &&
        notInSubscriptionsList.length > 0
      ) {
        return (
          notInSubscriptionsList?.map(apparatus => ({
            apparatusId: apparatus.apparatusId,
            type: apparatus.type,
            apparatusName: apparatus.name
          })) ?? []
        )
      }

      //make sure appartus(es) that are not in subscriptions list, are also not in legacy subscription list
      if (this.legacySubscriptionList?.length > 0) {
        notInSubscriptionsList = notInSubscriptionsList?.filter(
          x => !this.legacySubscriptionList?.find(y => y.apparatusId == x.apparatusId)
        )
      }

      return (
        notInSubscriptionsList?.map(apparatus => ({
          apparatusId: apparatus.apparatusId,
          type: apparatus.type,
          apparatusName: apparatus.name
        })) ?? []
      )
    },
    /** Returns whether or not the given apparatusId is included in a non-cancelled plan (this.subscriptions filters out cancelled plans). */
    hasActiveSubscription(apparatusId) {
      return this.subscriptions?.some(x => x.apparatuses?.find(y => y.id == apparatusId))
    },
    /** Returns whether or not the given apparatusId is included in a non-cancelled legacy plan (this.subscriptions filters out cancelled plans). */
    hasActiveLegacySubscription(apparatusId) {
      return this.legacySubscriptionList?.some(x => x?.apparatusId == apparatusId)
    },
    filterApparatusesFromState() {
      // don't include apparatuses that aren't fully enrolled (don't have a device) & for now don't include v1 tanks
      this.apparatuses = this.apparatusesFromState?.filter(apparatus =>
        apparatus.properties?.some(property => property.type === Constants.ApparatusPropertyDevice)
      )
    },
    apparatusWithMultipleSubscriptions() {
      if (this.apparatuses?.length > 0) {
        this.apparatuses.forEach(apparatus => {
          if (
            this.subscriptions.filter(
              subscription =>
                subscription.apparatuses.some(
                  subscriptionApparatus => subscriptionApparatus.id === apparatus.apparatusId
                ) && subscription.subscriptionStatus === Constants.SubscriptionDetailActive
            ).length > 1
          ) {
            this.apparatusMultiplePlans = apparatus
            this.premiumSubscriptionApparatusMultiplePlans = this.subscriptions.find(
              subscription =>
                subscription.apparatuses.some(
                  subscriptionApparatus => subscriptionApparatus.id === apparatus.apparatusId
                ) && subscription.subscriptionStore !== Constants.DealerSubscriptionStore
            )
            return
          }
        })
      }
    },
    calcProductsOnBasicSubscriptionOrNotInPlan() {
      this.productsOnBasicSubscriptionOrNotInPlan = this.apparatuses
        ?.filter(x => this.isApparatusBasicOrNotOnPlan(x) === true)
        .map(apparatus => {
          return {
            apparatusId: apparatus.apparatusId,
            apparatusName: apparatus.name,
            apparatusType: apparatus.type,
            country: apparatus.country
          }
        })
    },
    isNonDealerPremiumProduct(app) {
      return this.subscriptions.find(
        subscription =>
          subscription.subscriptionStore !== Constants.DealerSubscriptionStore &&
          subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled &&
          subscription.apparatuses.find(apparatus => apparatus.id === app.apparatusId)
      )
    },
    calcProductsOnPremiumSubscription() {
      this.apparatusesOnExistingSubscription = this.apparatuses
        ?.filter(x => this.isNonDealerPremiumProduct(x))
        .map(apparatus => {
          return {
            apparatusId: apparatus.apparatusId,
            apparatusName: apparatus.name,
            apparatusType: apparatus.type,
            country: apparatus.country
          }
        })
    },
    compileLegacySubscriptionList() {
      this.legacySubscriptionList = this.subscriptionDetails?.legacyBillingProducts?.products
        .filter(product =>
          this.apparatuses.some(
            apparatus =>
              apparatus?.apparatusId === product?.apparatusId &&
              (product?.status === Constants.SubscriptionActive ||
                product?.status === Constants.SubscriptionPendingCancellation ||
                product?.status === Constants.SubscriptionPendingTermination ||
                product?.status === Constants.SubscriptionPendingSuspension ||
                product?.status === Constants.SubscriptionDunning)
          )
        )
        .map(product => {
          const apparatus = this.apparatuses?.find(apparatus => apparatus?.apparatusId === product?.apparatusId)
          if (product != null && product != undefined) {
            return {
              apparatusId: apparatus.apparatusId,
              apparatusName: apparatus.name,
              apparatusType: apparatus.type,
              isDealerManaged: apparatus.isDealerManaged,
              dealerEmail: apparatus.preferredDealerEmail,
              subscriptionProductId: product?.productId,
              subscriptionProductName: product?.productName || '',
              subscriptionCost: product?.cost || 0,
              subscriptionDuration: product?.durationInMonths || 0,
              subscriptionNextBillingDate: product?.nextBillingDate || '',
              subscriptionStatus: product?.status,
              subscriptionDunningState: product?.dunningState,
              subscriptionGracePeriodEndDate: product?.gracePeriodEndDate || ''
            }
          }
        })
    },
    compileSubscriptionsList() {
      this.subscriptions = !this.subscriptionDetails //might be null first time thru
        ? []
        : this.subscriptionDetails?.subscriptions
            ?.filter(subscription => subscription?.status !== Constants.SubscriptionDetailCanceled)
            ?.map(subscription => {
              var apparatusList = []
              subscription?.apparatuses?.forEach(subApparatus => {
                const apparatus = this.apparatuses?.find(apparatus => apparatus.apparatusId == subApparatus.apparatusId)
                apparatusList.push({
                  id: apparatus?.apparatusId,
                  name: apparatus?.name,
                  type: apparatus?.type
                })
              })
              return {
                apparatuses: apparatusList,
                subscriptionCost: subscription?.product.cost || 0,
                subscriptionPendingCost: subscription?.pendingProduct?.cost || 0,
                subscriptionDurationInMonths: subscription?.product.durationInMonths || 0,
                subscriptionPendingDurationInMonths: subscription?.pendingProduct?.durationInMonths || 0,
                subscriptionProductId: subscription?.product.productId,
                subscriptionPendingProductId: subscription?.pendingProduct?.productId,
                subscriptionProductName: subscription?.product.name || '',
                subscriptionPendingProductName: subscription?.pendingProduct?.name || '',
                subscriptionRenewalDate: subscription?.renewalDate || '',
                subscriptionGracePeriodDate: subscription?.gracePeriodEndDate || '',
                subscriptionStore: subscription?.store,
                subscriptionStatus: subscription?.status,
                subscriptionReason: subscription?.reason,
                pendingProductId: subscription?.pendingProduct?.productId,
                pendingProductName: subscription?.pendingProduct?.name,
                pendingProductDescription: subscription?.pendingProduct?.description
              }
            })
    },
    compileDealerManagedSubscriptionList() {
      this.productsDealerManaged = this.apparatuses
        ?.filter(apparatus => apparatus.isDealerManaged)
        .map(apparatus => {
          return {
            apparatusId: apparatus.apparatusId,
            apparatusName: apparatus.name,
            apparatusType: apparatus.type,
            dealerInfo: {
              preferredDealerName: apparatus.preferredDealerName ?? '',
              preferredDealerEmail: apparatus.preferredDealerEmail ?? '',
              preferredDealerPhone: apparatus.preferredDealerPhone ?? ''
            }
          }
        })
    },
    hasPendingCanceledSub() {
      return this.subscriptionDetails?.subscriptions?.some(
        subscription => subscription.status === Constants.SubscriptionDetailPendingCancel
      )
    },
    checkAndClearCompletedPendingChanges,
    getExpiredDealerManagedApparatuses() {
      const expiredDealerManagedSubscriptions = this.subscriptionDetails?.subscriptions?.filter(
        sub => sub.status === Constants.SubscriptionDetailCanceled && sub.store === Constants.DealerSubscriptionStore
      )

      if (expiredDealerManagedSubscriptions && expiredDealerManagedSubscriptions.length > 0) {
        let apparatusesWithExpiredDealerManagedSubs = []
        expiredDealerManagedSubscriptions.forEach(sub => {
          let apparatus = this.apparatusesFromState?.find(
            apparatus =>
              apparatus.isDealerManaged && sub.apparatuses.some(app => app.apparatusId === apparatus.apparatusId)
          )
          if (apparatus !== undefined) {
            apparatusesWithExpiredDealerManagedSubs.push(apparatus)
          }
        })

        return apparatusesWithExpiredDealerManagedSubs
      } else {
        return []
      }
    },
    calculateSubscriptionEndDate
  },
  watch: {
    apparatusesFromState() {
      this.filterApparatusesFromState()
    },
    subscriptionDetails(newVal, oldVal) {
      if (newVal) {
        this.checkAndClearCompletedPendingChanges(newVal)
      }
      this.compileSubscriptionsList()
      this.compileLegacySubscriptionList()
    },
    subscriptions(newVal, oldVal) {
      this.getSubForPremiumPlanCard()
      this.apparatusWithMultipleSubscriptions()
      this.numberOfApparatusInNeedOfAttention = this.getNumberOfApparatusInNeedOfAttention()
      this.premiumApparatusesNeedsAttention = this.getPremiumApparatusesNeedsAttention()
      this.dunningSubscription = this.getDunningSubscription()
      this.expiredDealerManagedApparatuses = this.getExpiredDealerManagedApparatuses()
    },
    apparatuses() {
      this.compileSubscriptionsList()
      this.compileLegacySubscriptionList()
      this.calcProductsOnPremiumSubscription()
      this.apparatusWithMultipleSubscriptions()
      this.compileDealerManagedSubscriptionList()
      this.calcProductsOnBasicSubscriptionOrNotInPlan()
      this.numberOfApparatusInNeedOfAttention = this.getNumberOfApparatusInNeedOfAttention()
      this.premiumApparatusesNeedsAttention = this.getPremiumApparatusesNeedsAttention()
      this.dunningSubscription = this.getDunningSubscription()
    }
  },
  provide() {
    return {
      /** This is SubscriptionDetailData type (in ML mobile speak) rather than details of an individual subscription. */
      updateSubscriptionDetails: computed(() => newSubDetails => {
        this.subscriptionDetails = newSubDetails
      })
    }
  },
  async mounted() {
    if (this.apparatusesFromState == null || this.apparatusesFromState?.length == 0) {
      await this.$store.dispatch('getApparatuses')
    }
    if (!this.currentAppConfig) {
      await this.$store.dispatch('getApplicationConfiguration', 'Any')
    }

    this.filterApparatusesFromState()

    if (this.accountProfile) {
      await this.$store
        .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
        .then(response => {
          this.subscriptionDetails = response.data
          // Determine if the blue banner, if one is showing, should still be displayed or not based on the latest subscription data.
          this.checkAndClearCompletedPendingChanges(response.data)

          const purchaseDate = this.subscriptionDetails?.subscriptions[0]?.purchasedDate
          const duration = this.subscriptionDetails?.subscriptions[0]?.product?.durationInMonths
          if (purchaseDate && duration) {
            this.endDate = this.calculateSubscriptionEndDate(purchaseDate, duration)
          } else {
            this.endDate = null
          }
        })
        .catch(() => {})

      this.v1FuelMonitors = this.apparatuses?.filter(
        apparatus => this.isApparatusV1FuelTank(apparatus) && !apparatus.isDealerManaged
      )

      this.compileLegacySubscriptionList()
      this.compileSubscriptionsList()
      this.compileDealerManagedSubscriptionList()
      this.getSubForPremiumPlanCard()
      this.calcProductsOnBasicSubscriptionOrNotInPlan()
      this.calcProductsOnPremiumSubscription()
      this.apparatusWithMultipleSubscriptions()
      this.numberOfApparatusInNeedOfAttention = this.getNumberOfApparatusInNeedOfAttention()
      this.premiumApparatusesNeedsAttention = this.getPremiumApparatusesNeedsAttention()
      this.dunningSubscription = this.getDunningSubscription()
      this.isLoading = false
      this.expiredDealerManagedApparatuses = this.getExpiredDealerManagedApparatuses()
    } else {
      this.$store.dispatch('getAccountProfile')
    }
  }
}
</script>
<style scoped lang="scss">
.icon-and-text {
  flex-direction: row;
  display: flex;
  gap: 0.25em;
  align-items: center;
}

.collapsed .when-open,
.not-collapsed .when-closed {
  display: none;
}

.check-icon {
  color: var(--blue-700);
}

.receives-features-text {
  color: var(--carbon-600);
  font-size: 0.875em;
  line-height: 1.35;
}

.subs-info-card-body {
  padding: 0px !important;
}
.card-title {
  margin-bottom: 0;
}
.sub-radio-dot {
  font-size: 0.875rem !important;
}

.current-plan-badge {
  border: 2px solid;
  padding: 5px;
}

.pending-change-badge {
  padding: 5px;
}

#premium-enrollment-modal-step-1 {
  @media (min-width: 576px) {
    .modal-dialog {
      max-width: 200px !important;
    }
  }
  @media (min-width: 800px) {
    .modal-dialog {
      max-width: 400px !important;
    }
  }
}

.subscriptionCardText {
  font-size: 16px !important;
}

.features {
  margin-top: 0.5em;
  gap: 0.75em;
  display: flex;
  flex-direction: column;
}

.supporting-text {
  font-size: 0.875em;
  color: var(--gray-600);
  line-height: 1.25;
}

.legal-disclaimer {
  font-size: 0.875em;
  color: var(--carbon-600);
}

.bullet-points {
  li {
    margin-bottom: 0.35em;
  }
}

.shouldnt-take-long {
  text-align: center;
}

.verify-reading {
  font-style: italic;
  text-align: center;
}

.check-cellular {
  text-align: center;
}

.modal-template-header {
  padding: 160px 160px;
}

.bold-text {
  font-weight: bold;
}

li {
  margin: 0px 0;
}

.tu-help-alert {
  background-color: var(--yellow-200);
  color: var(--carbon-900);
  border: solid 1px var(--yellow-400);
}

.invalid-feedback {
  order: 2;
}
</style>
<style scoped lang="scss">
h1 {
  font-weight: normal;
  line-height: 1.15;
}

.paid-subs-info-card {
  border: 2px solid var(--carbon-300) !important;
  &:hover {
    border: 2px solid var(--brand-400) !important;
    background-color: transparent;
  }
  padding: 0 30px;
  border-radius: 8px;
}

.selected-sub-info-card {
  border: 2px solid var(--brand-400) !important;
  border-radius: 8px;
}

.card-title {
  margin-bottom: 0;
}

.selected {
  border: 1px solid var(--brand-400);
  background: white;
  box-shadow: 0px 0px 10px var(--brand-400);
}

.btn-subscription-radio {
  color: var(--carbon-900);
  font-size: 30px;
  font-weight: bold;
  padding: 0.375rem 2rem;
  border-color: var(--brand-600);
  border-width: 2px;
  margin-bottom: 10px;
}
.btn-subscription-radio:not(:disabled):not(.disabled):hover {
  background-color: var(--orange-200);
}
.btn-subscription-radio:not(:disabled):not(.disabled):focus:active {
  box-shadow: 0px 0px 8px rgba(255, 107, 0, 0.25) !important;
  border-width: 2px;
}
.btn-subscription-radio.active {
  box-shadow: 0 0 0 0.2rem rgba(255, 107, 0, 0.25) !important;
}

.sub-free-radio-text {
  font-size: 16px !important;
}

.feature {
  display: flex;
  align-items: center;
  gap: 0.5em;

  i {
    font-size: 1.125em;
    color: var(--carbon-600);
  }
  div {
    flex: 1;
  }
}
.basic-features {
  font-size: 0.875em;
  line-height: 1.35;
  margin-bottom: 0;
}

.premium-feature-text {
  font-weight: 600;
  font-size: 0.875em;
  line-height: 1.35;
  margin-bottom: 2px;
}
.premium-feature-supporting-text {
  font-size: 0.875em;
  line-height: 1.35;
  margin-bottom: 0;
  color: var(--carbon-600);
}
</style>
